import { cloneElement } from 'react';

import cx from 'classnames';

import { makeStyles } from '@mui/styles';
import Paper from '@mui/material/Paper';
import Collapse from '@mui/material/Collapse';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import IconButton from '@mui/material/IconButton';

const useStyles = makeStyles(
  ({ spacing, palette, transitions }) => ({
    root: {
      position: 'relative',
      borderRadius: 2,

      '$innerCollapseWrapper, &': {
        '& > *:not(:last-child)': {
          borderBottom: `1px solid ${palette.layout.line.divider}`,
        },
      },
    },
    icon: {
      color: palette.text.onMain,
    },
    expandIcon: {
      '& > *': {
        transform: 'rotate(-180deg)',
        transition: transitions.create('transform', {
          duration: transitions.duration.shortest,
        }),
      },
    },
    headerContainer: {
      position: 'relative',
    },
    header: {
      paddingTop: [spacing(3), '!important'],
    },
    headerAction: {
      zIndex: 1,
      position: 'absolute',
      top: 0,
      bottom: 0,
      right: 0,
      width: 48,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      pointerEvents: 'none',
    },
    innerCollapseWrapper: {},

    isCollapsed: {
      '& $expandIcon > *': {
        transform: 'rotate(0deg)',
      },
      '& $headerContainer': {
        borderBottom: 'none',
      },
    },
    hasColor: {
      '--main-bg-color': palette.layout.line.divider,
      position: 'relative',
      borderTopLeftRadius: 4,
      borderBottomLeftRadius: 4,

      '&::after': {
        pointerEvents: 'none',
        content: '" "',
        position: 'absolute',
        top: -1,
        left: -1,
        bottom: -1,
        width: '100%',
        borderRadius: 4,
        borderLeftWidth: 4,
        borderLeftStyle: 'solid',
        borderColor: 'var(--main-bg-color)',
      },
    },
    canCollapse: {
      '& $headerAction': {
        pointerEvents: 'auto',
      },
    },
    hasHeaderAction: {
      '& $header': {
        paddingRight: spacing(5),
      },
    },
  }),
  { classNamePrefix: 'StackableCard' }
);

export default function StackableCard({
  children = null,
  header,
  footer = null,
  color = null,
  isCollapsed = null,
  hasIcon = true,
  onToggleCollapse = () => {},
  translations = {
    collapseTogglerLabel: 'Show more information',
    ariaLabelLink: 'Go to',
  },
}) {
  const classes = useStyles();
  const hasHeaderLink = header.props.href || header.props.to;
  const canCollapse = !hasHeaderLink && isCollapsed !== null;
  const newHeader = cloneElement(header, {
    className: cx(header.props.className, classes.header),
  });

  return (
    <Paper
      style={color ? { '--main-bg-color': color } : null}
      elevation={0}
      variant="outlined"
      className={cx(classes.root, {
        [classes.hasHeaderAction]: canCollapse || hasHeaderLink,
        [classes.canCollapse]: canCollapse,
        [classes.isCollapsed]: isCollapsed && canCollapse,
        [classes.hasColor]: Boolean(color),
      })}
    >
      <div className={classes.headerContainer}>
        {newHeader}
        {hasIcon && (
          <div className={classes.headerAction}>
            {canCollapse && (
              <IconButton
                className={cx(classes.expandIcon, classes.icon)}
                onClick={onToggleCollapse}
                aria-label={translations.collapseTogglerLabel}
                size="large"
              >
                <ExpandMoreIcon />
              </IconButton>
            )}
            {hasHeaderLink && (
              <ChevronRightIcon
                className={classes.icon}
                data-testid="header-link-icon"
                aria-label={translations.ariaLabelLink}
              />
            )}
          </div>
        )}
      </div>

      {children && (
        <Collapse in={!isCollapsed}>
          <div className={classes.innerCollapseWrapper}>{children}</div>
        </Collapse>
      )}

      {footer}
    </Paper>
  );
}
